
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function BackupBatterySystemsPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Backup Battery System Installation',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Home backup battery system installation in Fresno, CA. Whole-home and partial backup power solutions for PG&E outages and blackouts.',
    url: `${siteUrl}/services/renewables-ev/backup-battery-systems`,
  };

  return (
    <>
      <SEOHead
        title="Backup Battery Systems Fresno CA | Home Power Backup"
        description="Professional backup battery system installation in Fresno, CA. Whole-home and partial backup power for PG&E outages. Tesla Powerwall and more."
        canonical="/services/renewables-ev/backup-battery-systems"
        keywords="backup battery system Fresno, home battery backup, power outage solution, emergency power"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Backup Battery Systems — Fresno, CA"
        heroSubtitle="Keep your home powered during PG&E outages"
        heroImage="https://public.readdy.ai/ai/img_res/4458890ac7ee74e3c74adc7fb003e528.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Close%20up%20of%20backup%20battery%20system%20control%20panel%20and%20automatic%20transfer%20switch%20with%20digital%20display%20showing%20battery%20status%20and%20backup%20circuits%20with%20circuit%20breakers%20and%20electrical%20connections%20visible%20professional%20California%20installation%20no%20people&width=700&height=600&seq=backup-battery-fresno-001&orientation=portrait"
        fresnoContent="PG&E power outages and California's Public Safety Power Shutoffs (PSPS) make backup battery systems essential for Fresno homes. Unlike generators, battery backup systems provide silent, automatic, emission-free power during outages. Our certified electricians design and install whole-home and partial backup systems throughout Fresno County including Clovis, Sanger, and surrounding areas. Whether you need to power essential circuits during brief outages or run your entire home including AC during extended blackouts, we create custom backup solutions. Pair with solar panels to qualify for the 30% federal tax credit and California SGIP rebates."
        features={[
          {
            icon: 'ri-flashlight-line',
            title: 'Automatic Backup Power',
            description: 'Seamless transition during outages in under 1 second',
          },
          {
            icon: 'ri-home-line',
            title: 'Whole-Home Backup',
            description: 'Power your entire home including AC and appliances',
          },
          {
            icon: 'ri-list-check',
            title: 'Essential Circuit Backup',
            description: 'Cost-effective backup for critical circuits only',
          },
          {
            icon: 'ri-volume-mute-line',
            title: 'Silent Operation',
            description: 'No noise or fumes unlike gas generators',
          },
          {
            icon: 'ri-stack-line',
            title: 'Scalable Capacity',
            description: 'Add batteries as your backup needs grow',
          },
          {
            icon: 'ri-smartphone-line',
            title: 'Remote Monitoring',
            description: 'Check battery status and power usage from anywhere',
          },
        ]}
        whatsIncluded={[
          'Free backup power assessment and system design',
          'Load calculation and critical circuit identification',
          'City of Fresno electrical permits and plan review',
          'Professional battery system installation',
          'Automatic transfer switch installation',
          'Critical loads panel or whole-home backup configuration',
          'Electrical panel modifications and circuit rewiring',
          'Battery mounting and proper ventilation',
          'Gateway and monitoring system setup',
          'Mobile app configuration and training',
          'Complete system testing with simulated outage',
          'PG&E notification and approval (if required)',
          'Federal tax credit documentation (if with solar)',
          'California SGIP rebate application assistance',
          '10-year electrical workmanship warranty',
        ]}
        faqs={[
          {
            question: 'How much does a backup battery system cost in Fresno?',
            answer:
              "Backup battery system costs in Fresno depend on your backup needs. Essential circuit backup (refrigerator, lights, outlets, WiFi) with one Tesla Powerwall costs $12,000-$15,000 installed. Whole-home backup including AC typically requires 2-3 Powerwalls costing $24,000-$40,000. However, California's SGIP rebate provides $2,700-$4,700 per Powerwall, and the 30% federal tax credit applies when paired with solar panels. Most Fresno homeowners pay $8,000-$12,000 net for essential circuit backup or $18,000-$30,000 for whole-home backup after incentives. We provide detailed quotes based on your specific backup requirements.",
          },
          {
            question: "What's the difference between whole-home and essential circuit backup?",
            answer:
              'Whole-home backup powers your entire electrical panel including AC, electric heating, pool pumps, and all appliances. It requires 2-4 batteries (27-54kWh) costing $24,000-$50,000 but provides complete normalcy during outages. Essential circuit backup powers only critical circuits (refrigerator, lights, outlets, WiFi, garage door) using 1-2 batteries (13.5-27kWh) costing $12,000-$25,000. For Fresno\'s hot summers, many homeowners choose a middle approach: essential circuits plus one AC unit, requiring 2 batteries. We help you prioritize circuits and design a backup system matching your needs and budget.',
          },
          {
            question: 'How long will a backup battery power my Fresno home during an outage?',
            answer:
              'Backup duration depends on battery capacity and your power consumption. Essential circuit backup (1 Powerwall, 13.5kWh) typically lasts 12-24 hours powering refrigerator, lights, WiFi, and outlets. Adding AC during Fresno summers reduces runtime to 4-8 hours. Whole-home backup (3 Powerwalls, 40.5kWh) powers everything including AC for 8-16 hours depending on usage. If paired with solar panels, batteries recharge during the day, providing indefinite backup during multi-day outages. We calculate expected runtime based on your actual usage patterns during our free assessment.',
          },
          {
            question: 'Are backup batteries better than generators for Fresno homes?',
            answer:
              'Backup batteries offer several advantages over generators for Fresno homes: automatic operation (no manual starting), silent operation (no noise complaints), zero emissions (safe for indoor installation), no fuel storage or maintenance, and seamless transition (under 1 second vs 10-30 seconds). Batteries also provide time-of-use savings during normal operation, unlike generators that sit idle. However, generators provide longer runtime for extended outages unless batteries are paired with solar. For most Fresno homeowners, battery backup provides better overall value, convenience, and daily utility compared to standby generators.',
          },
          {
            question: 'Can backup batteries power my AC during Fresno summer outages?',
            answer:
              'Yes, but it requires proper system sizing. Central AC units draw 3-5kW continuously, consuming a 13.5kWh Powerwall in 3-4 hours. For reliable AC backup during Fresno summers, we recommend 2-3 Powerwalls (27-40.5kWh) providing 8-12 hours of AC runtime. Alternatively, many Fresno homeowners backup only essential circuits plus one mini-split AC unit (1-1.5kW) for bedroom cooling, requiring just 1-2 Powerwalls. Paired with solar panels, batteries recharge during the day, enabling AC operation during multi-day outages. We design systems based on your cooling priorities and budget.',
          },
          {
            question: 'What rebates are available for backup battery systems in Fresno?',
            answer:
              "Fresno residents can access significant backup battery incentives. California's SGIP provides rebates of $200-$350 per kWh ($2,700-$4,700 for a 13.5kWh Powerwall). Homes in high fire-threat areas or low-income households qualify for enhanced SGIP rebates up to $1,000 per kWh ($13,500 per Powerwall). The 30% federal solar tax credit applies when batteries are installed with solar panels (new or existing systems). Combined incentives can cover 40-60% of total costs. We handle all rebate applications and documentation, maximizing your savings on backup battery installation.",
          },
        ]}
        ctaText="Get Your Free Backup Battery Quote"
      />
    </>
  );
}
